import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"

// Il valore di partenza serve a chi legge il tema fuori dal provider —
// un test che monta un pezzo solo, per dire: il chiaro è quello che
// l'app ha sempre avuto.
export const themeContext = React.createContext({
	mode: "auto",
	theme: "light",
	setMode: () => {},
})

export const useTheme = () => useContext(themeContext)

// Tre scelte, non due: chiaro, scuro, e "come il telefono". L'ultima è
// quella di partenza, perché chi ha messo il telefono scuro l'ha già detto
// una volta e non c'è motivo di rifarglielo dire.
export const THEME_MODES = ["auto", "light", "dark"]

const STORAGE_KEY = "incampo.theme"

// Il colore della barra di sistema: il fondo della pagina, in tutti e due i
// temi. Era l'arancione della fascia quando la fascia era attaccata in cima;
// da quando è un'isola sopra c'è la pagina, e una striscia arancione sopra
// la striscia chiara staccherebbe l'isola dal suo posto. Sono gli stessi valori di
// `theme.css` — qui servono scritti per esteso, perché il browser vuole un
// colore vero e non sa cosa farsene di una variabile.
const THEME_COLOR = { light: "#fff4ef", dark: "#0c171d" }

const prefersDark = () =>
	typeof window !== "undefined" &&
	window.matchMedia?.("(prefers-color-scheme: dark)").matches === true

const readStoredMode = () => {
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY)
		return THEME_MODES.includes(stored) ? stored : "auto"
	} catch {
		// Navigazione privata o cookie di terze parti bloccati: si riparte
		// dal telefono, che è comunque la scelta più probabile.
		return "auto"
	}
}

export const resolveTheme = mode => (mode === "auto" ? (prefersDark() ? "dark" : "light") : mode)

// L'unico punto che tocca il DOM. Lo chiama anche lo script in
// `public/index.html` a modo suo, prima che React esista: senza, la prima
// schermata sarebbe bianca per un istante anche a tema scuro, e quel lampo
// in faccia di notte è esattamente ciò da cui il tema scuro protegge.
const applyTheme = theme => {
	document.body.classList.toggle("dark", theme === "dark")
	document
		.querySelector('meta[name="theme-color"]')
		?.setAttribute("content", THEME_COLOR[theme])
}

const ThemeProvider = ({ children }) => {
	const [mode, setMode] = useState(readStoredMode)
	const [theme, setTheme] = useState(() => resolveTheme(readStoredMode()))

	const changeMode = useCallback(newMode => {
		if (!THEME_MODES.includes(newMode)) return
		setMode(newMode)
		try {
			window.localStorage.setItem(STORAGE_KEY, newMode)
		} catch {
			// La scelta vale per questa sessione: meglio di un errore in
			// faccia a chi voleva solo spegnere la luce.
		}
	}, [])

	useEffect(() => {
		setTheme(resolveTheme(mode))

		if (mode !== "auto") return

		// Da "auto" il tema può cambiare senza che nessuno tocchi l'app: il
		// telefono passa allo scuro al tramonto e l'app lo segue.
		const query = window.matchMedia("(prefers-color-scheme: dark)")
		const onChange = event => setTheme(event.matches ? "dark" : "light")
		query.addEventListener("change", onChange)
		return () => query.removeEventListener("change", onChange)
	}, [mode])

	useEffect(() => {
		applyTheme(theme)
	}, [theme])

	const value = useMemo(() => ({ mode, theme, setMode: changeMode }), [mode, theme, changeMode])

	return <themeContext.Provider value={value}>{children}</themeContext.Provider>
}

export default ThemeProvider
